import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Copy, Share2, Trophy, DollarSign, Users, TrendingUp, CheckCircle2, Clock } from "lucide-react";
import { api, fmtPrice } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const SITE = typeof window !== "undefined" ? window.location.origin : "https://endag1.com";

const TIER_COLORS = {
  Bronze: { chip: "bg-[#B8804D]/20 text-[#E9AE7F] border-[#B8804D]/40", bar: "bg-[#B8804D]" },
  Silver: { chip: "bg-[#B8BFC7]/15 text-[#DAE0E5] border-[#B8BFC7]/40", bar: "bg-[#B8BFC7]" },
  Gold:   { chip: "bg-[#F5C542]/15 text-[#F5C542] border-[#F5C542]/40", bar: "bg-[#F5C542]" },
};

function EnrollCard({ onEnrolled }) {
  const [busy, setBusy] = useState(false);
  const enroll = async () => {
    setBusy(true);
    try {
      await api.post("/partnership/join");
      toast.success("You're in — welcome to the partnership program!");
      onEnrolled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not enroll");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-white/10 bg-gradient-to-br from-[#4ADE80]/10 to-transparent p-8 md:p-12 text-center">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#4ADE80] mb-4">
        <Trophy className="h-4 w-4" /> Partnership Program
      </div>
      <h2 className="font-display text-3xl md:text-4xl text-[#F5F5F5] mb-3">Earn every time someone shops through your link</h2>
      <p className="text-[#A8A8A8] max-w-xl mx-auto leading-relaxed">
        Share your unique referral code. Get paid via e-Transfer or PayPal for every order that closes within 30 days of the click.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8 max-w-2xl mx-auto text-left">
        {[
          { name: "Bronze", refs: "0–9 orders", pay: "$10" },
          { name: "Silver", refs: "10–24 orders", pay: "$20" },
          { name: "Gold",   refs: "25+ orders", pay: "$30" },
        ].map((t) => (
          <div key={t.name} className="border border-white/10 p-4 bg-[#171717]">
            <div className={`inline-block text-[10px] uppercase tracking-[0.16em] px-2 py-1 border ${TIER_COLORS[t.name].chip}`}>{t.name}</div>
            <div className="mt-3 font-mono text-2xl text-[#F5F5F5]">{t.pay}<span className="text-xs text-[#A8A8A8]">/order</span></div>
            <div className="text-xs text-[#A8A8A8] mt-1">{t.refs}</div>
          </div>
        ))}
      </div>
      <Button
        data-testid="partnership-enroll-btn"
        onClick={enroll}
        disabled={busy}
        className="mt-8 bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998] px-8 py-6"
      >
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trophy className="h-4 w-4 mr-2" />}
        Join the partnership program
      </Button>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-white/10 p-5 bg-[#171717]">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#A8A8A8]">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-2 font-display text-2xl text-[#F5F5F5]">{value}</div>
      {sub && <div className="text-xs text-[#A8A8A8] mt-1">{sub}</div>}
    </div>
  );
}

function Dashboard({ stats, reload }) {
  const [referrals, setReferrals] = useState([]);
  const [method, setMethod] = useState(stats.payout_method || "etransfer");
  const [payoutEmail, setPayoutEmail] = useState(stats.payout_email || "");
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    api.get("/partnership/me/referrals").then(({ data }) => setReferrals(data)).catch(() => {});
  }, [stats.total_referrals]);

  const shareLink = `${SITE}/?ref=${stats.code}`;
  const tierName = stats.tier?.name || "Bronze";
  const tierColor = TIER_COLORS[tierName] || TIER_COLORS.Bronze;

  // Progress bar: how far the partner is toward the next tier's min_refs
  const nextT = stats.next_tier;
  const prevMin = stats.tier?.min_refs || 0;
  const nextMin = nextT?.min_refs || stats.total_referrals;
  const denom = Math.max(1, nextMin - prevMin);
  const progress = nextT ? Math.min(100, ((stats.total_referrals - prevMin) / denom) * 100) : 100;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "EndaG1", url: shareLink }); } catch {}
    } else {
      copy(shareLink, "Link");
    }
  };

  const savePayout = async () => {
    if (!payoutEmail) { toast.error("Enter a payout email"); return; }
    setSaving(true);
    try {
      await api.patch("/partnership/me/payout", { method, email: payoutEmail });
      toast.success("Payout details saved");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const requestPayout = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post("/partnership/me/request-payout");
      toast.success(`Payout requested — $${data.amount.toFixed(2)} pending`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not request payout");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header + tier */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#4ADE80]">Partnership Program</div>
          <h1 className="font-display text-3xl text-[#F5F5F5] mt-1">Your referral dashboard</h1>
        </div>
        <div className={`inline-flex items-center gap-2 border px-4 py-2 ${tierColor.chip}`} data-testid="partnership-tier-badge">
          <Trophy className="h-4 w-4" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] opacity-70">Tier</div>
            <div className="font-medium">{tierName} · ${stats.tier?.payout || 10}/order</div>
          </div>
        </div>
      </div>

      {/* Referral link */}
      <div className="border border-white/10 bg-[#171717] p-5 space-y-3">
        <div className="text-xs uppercase tracking-[0.16em] text-[#A8A8A8]">Your referral code</div>
        <div className="flex items-center gap-3 flex-wrap">
          <div data-testid="partnership-code" className="font-mono text-3xl md:text-4xl text-[#4ADE80] tracking-wider">
            {stats.code}
          </div>
          <Button
            data-testid="partnership-copy-code"
            variant="ghost"
            onClick={() => copy(stats.code, "Code")}
            className="border border-white/20 text-[#F5F5F5] hover:border-[#4ADE80] hover:text-[#4ADE80]"
          >
            <Copy className="h-4 w-4 mr-2" /> Copy
          </Button>
        </div>
        <div className="text-xs uppercase tracking-[0.16em] text-[#A8A8A8] pt-2">Shareable link</div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="partnership-share-link"
            readOnly
            value={shareLink}
            className="font-mono text-xs bg-transparent"
          />
          <Button
            data-testid="partnership-copy-link"
            variant="ghost"
            onClick={() => copy(shareLink, "Link")}
            className="border border-white/20 text-[#F5F5F5] hover:border-[#4ADE80] hover:text-[#4ADE80]"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            data-testid="partnership-share"
            variant="ghost"
            onClick={share}
            className="border border-white/20 text-[#F5F5F5] hover:border-[#4ADE80] hover:text-[#4ADE80]"
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="border border-white/10 bg-[#171717] p-5">
        <div className="flex items-center justify-between text-xs mb-3">
          <span className="text-[#A8A8A8] uppercase tracking-[0.16em]">Progress to next tier</span>
          <span className="text-[#F5F5F5] font-medium">
            {stats.total_referrals} / {nextT ? nextT.min_refs : stats.total_referrals} referrals
          </span>
        </div>
        <div className="h-2 bg-white/10 overflow-hidden">
          <div className={`h-full ${tierColor.bar} transition-all duration-700`} style={{ width: `${progress}%` }} />
        </div>
        <div className="text-xs text-[#A8A8A8] mt-3">
          {nextT
            ? <>Refer <span className="text-[#F5F5F5] font-medium">{stats.refs_to_next_tier} more</span> to unlock <span className="text-[#F5F5F5] font-medium">{nextT.name}</span> and earn <span className="text-[#4ADE80]">${nextT.payout}/order</span>.</>
            : "You've reached the top tier. Every referral now earns you $30."}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users}      label="Total referrals"     value={stats.total_referrals} />
        <StatCard icon={TrendingUp} label="Lifetime earned"     value={fmtPrice(stats.lifetime_earned || 0)} />
        <StatCard icon={Clock}      label="Pending balance"     value={fmtPrice(stats.pending_balance || 0)} sub={`${stats.pending_referrals || 0} orders`} />
        <StatCard icon={CheckCircle2} label="Paid out"          value={fmtPrice(stats.paid_balance || 0)} sub={`${stats.paid_referrals || 0} orders`} />
      </div>

      {/* Payout method */}
      <div className="border border-white/10 bg-[#171717] p-5 space-y-4">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-[#4ADE80]" />
          <span className="font-display text-lg">Payout method</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-[#A8A8A8]">Method</label>
            <select
              data-testid="partnership-method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full mt-1 bg-transparent border border-white/20 px-3 py-2 text-sm text-[#F5F5F5]"
            >
              <option value="etransfer">Interac e-Transfer</option>
              <option value="paypal">PayPal</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-[#A8A8A8]">
              {method === "paypal" ? "PayPal email" : "e-Transfer email"}
            </label>
            <Input
              data-testid="partnership-payout-email"
              type="email"
              value={payoutEmail}
              onChange={(e) => setPayoutEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end pt-2">
          <Button data-testid="partnership-save-payout" onClick={savePayout} disabled={saving} variant="ghost" className="border border-white/20 hover:border-[#4ADE80] hover:text-[#4ADE80]">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save
          </Button>
          <Button
            data-testid="partnership-request-payout"
            onClick={requestPayout}
            disabled={requesting || !stats.pending_balance || stats.pending_balance <= 0}
            className="bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998] disabled:opacity-50"
          >
            {requesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Request payout {stats.pending_balance ? `(${fmtPrice(stats.pending_balance)})` : ""}
          </Button>
        </div>
      </div>

      {/* Recent referrals */}
      <div className="border border-white/10 bg-[#171717] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <span className="font-display text-lg">Recent referrals</span>
          <span className="text-xs text-[#A8A8A8]">{referrals.length}</span>
        </div>
        {referrals.length === 0 ? (
          <div className="p-10 text-center text-[#A8A8A8] text-sm">
            No referrals yet. Share your link to start earning.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.16em] text-[#A8A8A8]">
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Order</th>
                <th className="text-right px-4 py-2">Value</th>
                <th className="text-center px-4 py-2">Tier</th>
                <th className="text-right px-4 py-2">You earned</th>
                <th className="text-center px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((r) => (
                <tr key={r.id} data-testid={`referral-row-${r.id}`} className="border-b border-white/5">
                  <td className="px-4 py-2 text-[#A8A8A8]">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-2 font-mono text-xs text-[#A8A8A8]">{(r.order_session_id || "").slice(0, 12)}…</td>
                  <td className="px-4 py-2 text-right text-[#A8A8A8]">{fmtPrice(r.order_amount || 0)}</td>
                  <td className="px-4 py-2 text-center text-xs">{r.tier_name}</td>
                  <td className="px-4 py-2 text-right font-mono text-[#4ADE80]">{fmtPrice(r.commission_amount || 0)}</td>
                  <td className="px-4 py-2 text-center text-xs">
                    {r.status === "paid"
                      ? <span className="text-[#4ADE80]">Paid</span>
                      : <span className="text-[#A8A8A8]">Pending</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function PartnershipSection() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partnership/me");
      setStats(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !stats) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4ADE80]" /></div>;
  }
  if (!stats.enrolled) return <EnrollCard onEnrolled={load} />;
  return <Dashboard stats={stats} reload={load} />;
}
